import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { useSiteSettings } from '../hooks/useSiteSettings';

const Navbar = () => {
  const siteSettings = useSiteSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: language === 'en' ? 'HOME' : 'ACCUEIL' },
    { path: '/demander-un-pret-auto', label: language === 'en' ? 'APPLY' : 'DEMANDER UN PRÊT' },
    ...(siteSettings?.inventory_enabled ? [{ path: '/nos-vehicules', label: language === 'en' ? 'OUR VEHICLES' : 'NOS VÉHICULES' }] : []),
    { path: '/verifier-admissibilite', label: language === 'en' ? 'QUICK PRE-QUALIFICATION' : 'PRÉQUALIFICATION RAPIDE' },
    { path: '/calculateur-pret-auto', label: language === 'en' ? 'CALCULATOR' : 'CALCULATEUR' },
    { 
      label: language === 'en' ? 'ABOUT' : 'À PROPOS',
      children: [
        { path: '/qui-sommes-nous', label: language === 'en' ? 'ABOUT US' : 'QUI SOMMES-NOUS ?' },
        { path: '/retablir-son-credit', label: language === 'en' ? 'REBUILD YOUR CREDIT' : 'RÉTABLIR SON CRÉDIT' },
        { path: '/cote-de-credit-pret-auto', label: language === 'en' ? 'CREDIT SCORE & CAR LOANS' : 'COTE DE CRÉDIT ET PRÊT AUTO' },
        { path: '/blog', label: language === 'en' ? 'BLOG' : 'BLOGUE' },
        { path: '/notre-equipe', label: language === 'en' ? 'OUR TEAM' : 'NOTRE ÉQUIPE' },
        { path: '/contact', label: language === 'en' ? 'CONTACT US' : 'CONTACTEZ-NOUS' }
      ]
    }
  ];

  const [openDropdown, setOpenDropdown] = useState(false);

  return (
    <>
      {/* Top Utility Bar */}
      <div className="fixed top-0 left-0 right-0 z-[51] bg-[#0b130e] text-white h-9 hidden md:flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex items-center justify-between text-xs">
          <div className="flex items-center gap-5">
            <a href="tel:+15144183968" className="flex items-center gap-1.5 hover:text-green-300 transition-colors">
              <Phone className="w-3 h-3" /> 514-418-3968
            </a>
            <a href="mailto:info@creditautoreset.ca" className="flex items-center gap-1.5 hover:text-green-300 transition-colors">
              <span>info@creditautoreset.ca</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://www.facebook.com/creditautoreset" target="_blank" rel="noopener noreferrer" className="hover:text-green-300 transition-colors">Facebook</a>
            <a href="https://www.instagram.com/creditautoreset" target="_blank" rel="noopener noreferrer" className="hover:text-green-300 transition-colors">Instagram</a>
            <button onClick={toggleLanguage} className="flex items-center gap-1 hover:text-green-300 transition-colors">
              <Globe className="w-3 h-3" /> {language === 'en' ? 'FR' : 'EN'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-300 md:top-9 top-0 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-md'
        }`}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center group shrink-0 mr-3">
            <div className="transform group-hover:scale-110 transition-transform duration-300">
              <img 
                src="/images/logo.png?v=3" 
                alt="Credit Auto Reset - Rétablir. Rouler. Réussir." 
                className="h-20 w-auto"
                style={{ mixBlendMode: 'darken' }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link, idx) => (
              link.children ? (
                <div key={idx} className="relative" onMouseEnter={() => setOpenDropdown(true)} onMouseLeave={() => setOpenDropdown(false)}>
                  <button className="px-2 xl:px-4 py-2.5 rounded-xl text-xs xl:text-[15px] whitespace-nowrap font-bold tracking-wide transition-all duration-300 text-gray-700 hover:text-[#1a5c3b] flex items-center gap-1">
                    {link.label}
                    <svg className={`w-3 h-3 transition-transform ${openDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {openDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-200 py-2 min-w-[200px] z-50">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block px-5 py-3 text-sm whitespace-nowrap font-bold tracking-wide text-gray-700 hover:text-[#1a5c3b] hover:bg-slate-50 transition-all"
                          onClick={() => setOpenDropdown(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-2 xl:px-4 py-2.5 rounded-xl text-xs xl:text-[15px] whitespace-nowrap font-bold tracking-wide transition-all duration-300 relative overflow-hidden group ${ 
                    location.pathname === link.path
                      ? 'bg-[#1a5c3b] text-white shadow-lg'
                      : 'text-gray-700 hover:text-[#1a5c3b]'
                  }`}
                >
                  {location.pathname !== link.path && (
                    <span className="absolute inset-0 bg-[#1a5c3b]/5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-xl"></span>
                  )}
                  <span className="relative z-10">{link.label}</span>
                </Link>
              )
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:514-418-3968"
              className="flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-gray-50 text-gray-700 hover:bg-[#1a5c3b]/10 hover:text-[#1a5c3b] transition-all duration-300 border border-gray-200 hover:border-[#1a5c3b]/30"
            >
              <Phone className="w-4 h-4" />
              <span className="font-bold whitespace-nowrap hidden xl:inline">514-418-3968</span>
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-4 py-2.5 rounded-xl hover:bg-gray-50 font-semibold"
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </Button>
            <Link
              to="/admin"
              className="flex items-center space-x-1 px-2 py-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-300 text-xs"
            >
              <Lock className="w-3 h-3" />
              <span>Admin</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-300 border border-gray-200"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <div className="px-4 py-6 space-y-2">
            {navLinks.map((link, idx) => (
              link.children ? (
                <div key={idx} className="space-y-1">
                  <div className="px-5 py-2 text-xs font-bold tracking-wider text-gray-400">{link.label}</div>
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-5 py-3 rounded-xl text-sm font-bold tracking-wide transition-all ${
                        location.pathname === child.path
                          ? 'bg-[#1a5c3b] text-white'
                          : 'text-gray-700 hover:bg-[#1a5c3b]/10 hover:text-[#1a5c3b]'
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-5 py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'bg-[#1a5c3b] text-white shadow-lg'
                      : 'text-gray-700 hover:bg-[#1a5c3b]/10 hover:text-[#1a5c3b]'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <a
                href="tel:514-418-3968"
                className="flex items-center space-x-3 px-5 py-3 rounded-xl bg-gray-50 text-gray-700 hover:bg-[#1a5c3b]/10 hover:text-[#1a5c3b] transition-all border border-gray-200"
              >
                <Phone className="w-5 h-5" />
                <span className="font-bold text-base">514-418-3968</span>
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold"
              >
                <Globe className="w-5 h-5" />
                <span className="text-base">{language === 'en' ? 'Français' : 'English'}</span>
              </Button>
              <Link
                to="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all text-sm"
              >
                <Lock className="w-3 h-3" />
                <span>Admin</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;